import { useState } from "react";
import { createSkillInlineAction } from "@/actions/skills";
import { InlineCreate } from "@/components/InlineCreate";
import styles from "./SkillFields.module.css";

export type SkillChoice = {
  id: number;
  code: string;
  label: string | null;
  // What counts toward the skill. Null only where the default-locale
  // translation is missing — the column itself is NOT NULL.
  description: string | null;
};

type SkillFieldsProps = {
  skills: SkillChoice[];
  selectedIds: number[];
  errors?: string[];
};

// The controls only, with no form of their own: the create page posts them
// alongside the template row, and the detail page wraps them in a save form of
// its own. A skill list is short and closed, so checkboxes still read better
// here than a search — unlike traits, which the catalog grows without bound.
export const SkillFields = ({
  skills,
  selectedIds,
  errors,
}: SkillFieldsProps) => {
  const [checkedIds, setCheckedIds] = useState(selectedIds);
  // Seeded from the server list and appended to locally, because a skill
  // minted from here must appear without a revalidate — the template being
  // written is uncommitted state that a route refresh would throw away.
  const [choices, setChoices] = useState(skills);

  const toggle = (id: number, isChecked: boolean) => {
    setCheckedIds((current) =>
      isChecked ? [...current, id] : current.filter((value) => value !== id),
    );
  };

  // A skill minted mid-authoring is one the author wants bound; leaving it
  // unchecked would only ask the same question twice.
  const addChoice = (choice: SkillChoice) => {
    setChoices((current) => [...current, choice]);
    setCheckedIds((current) => [...current, choice.id]);
  };

  return (
    <div className={styles.group}>
      <div className={styles.choices}>
        {choices.map((skill) => {
          const tipId = `skill-tip-${skill.id}`;
          const hasDescription = skill.description !== null;

          return (
            // The tip opens on hover and on focus-within rather than through a
            // trigger of its own: a control per skill would double the width of
            // a row that is meant to be scanned, and tabbing to the checkbox
            // has to reveal the same sentence a pointer does.
            <label key={skill.id} className={styles.choice}>
              <input
                type="checkbox"
                name="skillIds"
                value={String(skill.id)}
                checked={checkedIds.includes(skill.id)}
                onChange={(event) => toggle(skill.id, event.target.checked)}
                aria-describedby={hasDescription ? tipId : undefined}
              />
              {skill.label ?? skill.code}
              {hasDescription ? (
                <span id={tipId} role="tooltip" className={styles.tip}>
                  {skill.description}
                </span>
              ) : null}
            </label>
          );
        })}
      </div>
      <InlineCreate
        triggerLabel="New skill"
        title="Create a skill"
        note="Skills are the progression axis, and progression counts distinct completed templates per skill. Two spellings of one skill split that count and every chart reading it. Mint one only when nothing above is the same thing."
        labelPlaceholder="Reading"
        codePlaceholder="READING"
        descriptionLabel="Description"
        descriptionPlaceholder="What counts toward this skill."
        existing={choices}
        action={createSkillInlineAction}
        onCreated={addChoice}
      />

      {errors === undefined ? null : (
        <p className={styles.error} role="alert">
          {errors.join(" ")}
        </p>
      )}
    </div>
  );
};
